import React, { useRef, useMemo } from 'react';
import './ItemView.css';
import StatsList from '../StatsList';
import { processItemStats, processItemRequirementText } from '../../core/statProcessing';
import { useOverlay } from '../../contexts/OverlayContext';
import { useGeneratorContext } from '../../contexts/GeneratorStateContext';
import SearchService from '../../services/SearchService';
import SetView from './SetView';
import SetHeader from './SetHeader';
import OverlayEquipFooter from '../OverlayEquipFooter';
import { useEquipItem } from '../../hooks/useEquipItem';
import { buildElementIconFilter } from '../utils/CardGradient';
import { WeaponDamageList, WeaponMetaList } from '../weapon/WeaponSummary';
import { hasWeaponDamages, buildWeaponMetaRows } from '../../core/weapon';
import { applyWeaponMagingPreview } from '../../core/weaponMaging';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import MediaImg from '../utils/MediaImg';
import {
    hasItemEffect,
    getItemEffectName,
    getItemEffectDescription,
    ITEM_EFFECT_ICON
} from '../../core/itemEffect';

const ItemView = ({ item, lang = 'fr', compact = false, weaponMaging = null }) => {
    const { showOverlay } = useOverlay();
    const { equipItem, equipSet } = useEquipItem(lang);
    const { build } = useGeneratorContext();
    const containerRef = useRef(null);

    const gradientLayerStyle = useMemo(() => {
        const map = item?.effects_map || {};
        return {
            '--item-element-filter-icon': buildElementIconFilter(map),
        };
    }, [item]);

    const statRows = processItemStats(item?.effects_map || {});
    const rawWeapon = item?.weapon || null;
    const weapon = rawWeapon && weaponMaging
        ? applyWeaponMagingPreview(rawWeapon, weaponMaging)
        : rawWeapon;
    const hasWeaponDamage = rawWeapon && hasWeaponDamages(rawWeapon);
    const hasWeaponMeta = rawWeapon && buildWeaponMetaRows(rawWeapon, lang).length > 0;
    const setName = item?.item_set_id && item.item_set_name
        ? (item.item_set_name[lang] || (typeof item.item_set_name === 'object' ? (item.item_set_name.fr || item.item_set_name.en) : item.item_set_name))
        : null;
    const description = item?.description
        ? (typeof item.description === 'object' ? (item.description[lang] || item.description.fr || item.description.en) : item.description)
        : null;

    const hasEffect = hasItemEffect(item);
    const effectNameRaw = hasEffect ? getItemEffectName(item, lang) : '';
    const effectDescriptionRaw = hasEffect ? getItemEffectDescription(item, lang) : '';

    let effectTitle = effectNameRaw || '';
    let effectBody = '';

    if (effectDescriptionRaw) {
        const lines = String(effectDescriptionRaw).split('\n');
        const firstLine = lines[0]?.trim() || '';
        const normalizedFirst = firstLine.replace(/[:：]\s*$/, '').trim();
        const normalizedName = effectNameRaw ? String(effectNameRaw).trim() : '';

        if (normalizedName && normalizedFirst && normalizedFirst.toLowerCase() === normalizedName.toLowerCase()) {
            effectTitle = effectNameRaw;
            effectBody = lines.slice(1).join('\n');
        } else if (!effectTitle && firstLine) {
            effectTitle = firstLine;
            effectBody = lines.slice(1).join('\n');
        } else {
            effectBody = effectDescriptionRaw;
        }
    }

    const showEffectSection = hasEffect && (effectTitle || effectBody);

    // Process criteria if available
    const criteriaProcessed = useMemo(() => {
        if (!item) return null;
        return processItemRequirementText(item, lang, build?.lvl);
    }, [item, lang, build?.lvl]);

    const handleSetClick = async (e) => {
        e.stopPropagation();
        if (item?.item_set_id) {
            try {
                const set = await SearchService.getSet(item.item_set_id, lang);
                showOverlay(
                    [
                        <SetHeader key={`set-hdr-${item.item_set_id}`} set={set} lang={lang} />,
                        <SetView
                            key={`set-view-${item.item_set_id}`}
                            set={set}
                            lang={lang}
                            onItemClick={(it) => equipItem(it, null)}
                        />,
                    ],
                    {
                        footer: (
                            <OverlayEquipFooter
                                lang={lang}
                                onEquip={() => equipSet(set, null)}
                            />
                        ),
                    }
                );
            } catch (error) {
                console.error("Failed to fetch set details:", error);
            }
        }
    };

    if (!item) return null;

    return (
        <div className="item-view column" ref={containerRef}>
            <div className="item-view-gradient" style={gradientLayerStyle}>
                <div className="item-view-header column">
                    <MediaImg
                        className="item-view-icon"
                        src={item.icon ? resolveMediaUrl(item.icon) : '/img/stat_re_armes.png'}
                        alt={item.name && typeof item.name === 'object' ? (item.name[lang] || item.name.fr || item.name.en) : (item.name || '')}
                        resolve={false}
                        retry={Boolean(item.icon)}
                    />
                    <h5 className="item-view-name" lang={lang}>{item.name && typeof item.name === 'object' ? (item.name[lang] || item.name.fr || item.name.en) : (item.name || '')}</h5>
                    {setName && <button className="item-view-set-button" onClick={handleSetClick}>{setName}</button>}
                </div>
                <div className="item-view-content">
                    {hasWeaponDamage && (
                        <WeaponDamageList
                            weapon={weapon}
                            lang={lang}
                            className="item-view-weapon-damages"
                        />
                    )}
                    {hasWeaponDamage && (statRows?.length > 0 || showEffectSection) && (
                        <div className="item-view-divider" />
                    )}
                    <div className="item-view-main-stats column">
                        <StatsList stats={statRows} className="item-view-stats-col" lang={lang} />
                    </div>
                    {showEffectSection && (
                        <>
                            <div className="item-view-divider item-view-legendary-divider" />
                            <div className="item-view-legendary-row">
                                <img
                                    className="item-view-legendary-icon"
                                    src={ITEM_EFFECT_ICON}
                                    alt={effectTitle}
                                />
                                <span className="item-view-legendary-label">
                                    {effectTitle}
                                </span>
                            </div>
                            {effectBody && (
                                <div className="item-view-legendary-description">
                                    {effectBody}
                                </div>
                            )}
                        </>
                    )}
                    {hasWeaponMeta && (
                        <>
                            <div className="item-view-divider" />
                            <WeaponMetaList
                                weapon={rawWeapon}
                                lang={lang}
                                className="item-view-weapon-meta"
                            />
                        </>
                    )}

                    {criteriaProcessed && (
                        <>
                            <div className="item-view-divider criteria" />
                            <div className="item-view-conditions">
                                <div className='item-view-conditions-text'>{criteriaProcessed}</div>
                            </div>
                        </>
                    )}

                    {description && (
                        <>
                            <div className="item-view-divider" />
                            <div className="item-view-description">
                                {description}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ItemView;
